import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Send, Mountain, Loader2, Copy, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WHATSAPP_NUMBERS = {
  srirangam: '918944932312',
  villaaavaas: '919882259876',
  browndipper: '919050266563',
  bluehouse: '919891355963',
  eaglenest: '919816423344',
  tra: '918851657804',
  homestead: '918445439374',
  rajucottage: '919495833124',
};

export default function SocialGenerator() {
  const [properties, setProperties] = useState([]);
  const [downloading, setDownloading] = useState({});
  const [copied, setCopied] = useState({});
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetch(`${API}/api/admin/social/list`)
      .then(r => r.json())
      .then(d => setProperties(d.properties || []))
      .catch(console.error);
  }, []);

  const downloadCard = async (slug) => {
    setDownloading(prev => ({ ...prev, [slug]: true }));
    try {
      const res = await fetch(`${API}/api/admin/social/generate/${slug}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gohimalayan-${slug}-instagram.jpg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    } finally {
      setDownloading(prev => ({ ...prev, [slug]: false }));
    }
  };

  const copyCaption = (slug, caption) => {
    navigator.clipboard.writeText(caption).then(() => {
      setCopied(prev => ({ ...prev, [slug]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [slug]: false })), 2000);
    });
  };

  const sendToHost = (slug, name, caption) => {
    const number = WHATSAPP_NUMBERS[slug] || '919871155963';
    const msg = `Hi! We've featured ${name} on GoHimalayan. Here's your Instagram-ready caption — feel free to post it on your page:\n\n${caption}\n\nDownload your creative: gohimalayan.com/collective/${slug}`;
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white" data-testid="social-generator-page">
      <nav className="sticky top-0 z-50 bg-[#0d0d0d]/90 backdrop-blur-md border-b border-white/5 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/discovered" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs tracking-wider uppercase">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <Mountain className="w-5 h-5 text-white/40" />
            <span className="text-sm tracking-wider uppercase text-white/60 font-medium">Instagram Creatives</span>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 pt-10 pb-6">
        <h1 className="text-2xl md:text-3xl font-light italic text-white/90 mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Social Media Creatives
        </h1>
        <p className="text-white/40 text-sm max-w-xl">
          Download the creative, copy the caption, post on @gohimalayan. Hit "Send to Host" to WhatsApp the caption directly to the property owner for a collab post.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-20">
        <div className="space-y-6">
          {properties.map(prop => (
            <div key={prop.slug} className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden hover:border-white/10 transition-colors" data-testid={`social-card-${prop.slug}`}>
              <div className="flex flex-col md:flex-row">
                {/* Card preview */}
                <div className="md:w-[340px] flex-shrink-0">
                  <div className="aspect-square relative bg-black/30">
                    <img
                      src={`${API}/api/admin/social/generate/${prop.slug}`}
                      alt={`${prop.name} Instagram card`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>

                {/* Caption + Actions */}
                <div className="flex-1 p-5 md:p-6 flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-white/90 text-base font-medium">{prop.name}</p>
                      <p className="text-white/30 text-xs">{prop.location?.toLowerCase()} · {prop.badge}</p>
                    </div>
                  </div>

                  {/* Caption preview */}
                  <div className="flex-1 mb-4">
                    <p className="text-[11px] uppercase tracking-widest text-white/25 mb-2">Instagram Caption</p>
                    <div className="bg-black/30 rounded-lg p-3 border border-white/5">
                      <p className="text-white/60 text-xs leading-relaxed whitespace-pre-line">
                        {expanded[prop.slug] ? prop.caption : (prop.caption?.slice(0, 180) + '...')}
                      </p>
                      <button
                        onClick={() => setExpanded(prev => ({ ...prev, [prop.slug]: !prev[prop.slug] }))}
                        className="text-white/30 text-[10px] mt-2 hover:text-white/50 uppercase tracking-wider"
                      >
                        {expanded[prop.slug] ? 'Show less' : 'Read full caption'}
                      </button>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => downloadCard(prop.slug)}
                      disabled={downloading[prop.slug]}
                      className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 text-xs tracking-wider uppercase transition-colors disabled:opacity-50"
                      data-testid={`download-btn-${prop.slug}`}
                    >
                      {downloading[prop.slug] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      Download
                    </button>

                    <button
                      onClick={() => copyCaption(prop.slug, prop.caption)}
                      className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 text-xs tracking-wider uppercase transition-colors"
                      data-testid={`copy-btn-${prop.slug}`}
                    >
                      {copied[prop.slug] ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied[prop.slug] ? 'Copied' : 'Copy Caption'}
                    </button>

                    <button
                      onClick={() => sendToHost(prop.slug, prop.name, prop.caption)}
                      className="flex items-center gap-2 px-4 py-2.5 bg-[#25D366]/15 hover:bg-[#25D366]/25 rounded-lg text-[#25D366]/90 text-xs tracking-wider uppercase transition-colors"
                      data-testid={`share-btn-${prop.slug}`}
                    >
                      <Send className="w-3.5 h-3.5" />
                      Send to Host
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
